import { CustomResponse } from './RestaurantPrinterTypes';

export type { CustomResponse };

export type RestaurantOrderStatus =
  | 'OPEN'
  | 'CONFIRMED'
  | 'IN_KITCHEN'
  | 'READY'
  | 'BILLING'
  | 'CANCELLED'
  | 'CLOSED';
export type RestaurantOrderItemStatus = 'ACTIVE' | 'CANCELLED';
export type RestaurantOrderServiceType = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY';

export interface RestaurantOrderItemDTO {
  id: number;
  menuItemId: number | null;
  comboMenuId?: number | null;
  comboComponentIndex?: number | null;
  comboInstanceId?: number | string | null;
  comboName?: string | null;
  comboQuantity?: number | null;
  menuItemName?: string;
  itemName?: string;
  productName?: string;
  quantity: number;
  sentToKitchen: boolean;
  editable: boolean;
  checkId?: number | null;
  checkLabel?: string | null;
  unitPrice?: number;
  price?: number;
  subtotal?: number;
  total?: number;
  tourismTaxRate?: number;
  tourismTaxAmount?: number;
  tipPercentageApplied?: number;
  tipAmount?: number;
  notes: string | null;
  status: RestaurantOrderItemStatus;
  modifiers?: Array<{
    id: number;
    modifierId: number;
    name: string;
    priceAdjustment: number;
    rawMaterialId?: number | null;
    rawMaterialName?: string | null;
    consumptionQuantity?: number | null;
    consumptionUnitId?: number | null;
    consumptionUnitCode?: string | null;
  }>;
}

export interface RestaurantOrderDTO {
  id: number;
  branchId: number;
  orderNumber?: string;
  tableId: number | null;
  tableName?: string;
  tableCode?: string;
  diningRoomId?: number | null;
  diningRoomName?: string | null;
  customerId: number | null;
  customerName?: string | null;
  waiterId: number | null;
  waiterName?: string | null;
  notes: string | null;
  status: RestaurantOrderStatus;
  serviceType: RestaurantOrderServiceType;
  items: RestaurantOrderItemDTO[];
  subtotal?: number;
  total?: number;
  totalTourismTax?: number;
  totalTip?: number;
  createdAt?: string;
  confirmedAt?: string | null;
  closedAt?: string | null;
  reservationId?: number | null;
  reservationNumber?: string | null;
  kitchenOrderId?: number | null;
  kitchenPrintRequired?: boolean;
  mergedIntoOrderId?: number | null;
  checks?: RestaurantOrderCheckDTO[];
}

export type RestaurantOrderCheckStatus = 'OPEN' | 'BILLING' | 'SETTLED';

export interface RestaurantOrderCheckDTO {
  id: number;
  label: string;
  position: number;
  status: RestaurantOrderCheckStatus;
  itemIds: number[];
  subtotal: number;
  discountTotal: number;
  total: number;
  saleId?: number | null;
  billingStatus?:
    | 'PENDING'
    | 'FINALIZED'
    | 'ACCEPTED'
    | 'REJECTED'
    | 'CANCELLED'
    | null;
}

export interface RestaurantOrderSummaryDTO {
  id: number;
  orderNumber: string;
  status: RestaurantOrderStatus;
  tableId?: number | null;
  tableCode?: string | null;
  tableName?: string | null;
  diningRoomId?: number | null;
  diningRoomName?: string | null;
  customerName?: string | null;
  waiterName?: string | null;
  total?: number;
  activeItemCount?: number;
  openedAt?: string;
}

export interface CreateRestaurantOrderRequest {
  tableId?: number | null;
  customerId?: number | null;
  waiterId?: number | null;
  notes: string | null;
  reservationId?: number | null;
  serviceType: RestaurantOrderServiceType;
}

interface AddRestaurantOrderItemRequestBase {
  modifierIds: number[];
  quantity: number;
  notes: string | null;
}

export type AddRestaurantOrderItemRequest =
  | (AddRestaurantOrderItemRequestBase & {
      menuItemId: number;
      comboMenuId: null;
    })
  | (AddRestaurantOrderItemRequestBase & {
      menuItemId: null;
      comboMenuId: number;
      modifierIds: [];
    });

export type RestaurantMenuAvailabilityFilter =
  | 'ALL'
  | 'AVAILABLE'
  | 'UNAVAILABLE';

export interface RestaurantOrderMenuFilters {
  availability: RestaurantMenuAvailabilityFilter;
  featuredOnly: boolean;
  priceMin: string;
  priceMax: string;
}

export const EMPTY_MENU_FILTERS: RestaurantOrderMenuFilters = {
  availability: 'AVAILABLE',
  featuredOnly: false,
  priceMin: '',
  priceMax: '',
};

export const countActiveMenuFilters = (
  filters: RestaurantOrderMenuFilters
): number =>
  (filters.availability !== 'AVAILABLE' ? 1 : 0) +
  (filters.featuredOnly ? 1 : 0) +
  (filters.priceMin.trim() ? 1 : 0) +
  (filters.priceMax.trim() ? 1 : 0);

export interface RestaurantPartyOption {
  id: number;
  code?: string | null;
  name: string;
  defaultCustomer?: boolean;
}
